import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { Quote } from '../../shared/models/quote.interface';
import { User } from '../../shared/models/user.interface';
import { Office } from '../../shared/models/office.interface';
import { Customer } from '../../shared/models/customer.interface';
import { LeadSourceType } from '../../shared/models/lead.source.type.interface';
import { JobType } from '../../shared/models/job.type.interface';
import { QuoteSummary } from '../../quote/models/quote.summary.interface';
import { LeadFilter } from '../models/lead.filter';
import { DealerLeadSummary } from '../list/list.component';

@Injectable()

export class LeadService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getSummary(filter: LeadFilter): Observable<DealerLeadSummary> {
    let params = new HttpParams();

    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.dateRange) params = params.append('dateRange', filter.dateRange.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.probability) params = params.append('probability', filter.probability);
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId);
    if (filter.pastDue) params = params.append('pastDue', filter.pastDue.toString());
    if (filter.customerId) params = params.append('customerId', filter.customerId.toString());

    return this.httpGet<DealerLeadSummary>("/lead/summary?" + params.toString());
  }

  getList(filter: LeadFilter, sortColumn: string, sortDirection: string, page: number, pageSize: number = 25): Observable<Quote[]> {
    let params = new HttpParams();

    if (page != null) params = params.append('page', page.toString());
    if (pageSize != null) params = params.append('pageSize', pageSize.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.dateRange) params = params.append('dateRange', filter.dateRange.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.probability) params = params.append('probability', filter.probability);
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId);
    if (filter.pastDue) params = params.append('pastDue', filter.pastDue.toString());
    if (filter.customerId) params = params.append('customerId', filter.customerId.toString());

    return this.httpGet<Quote[]>("/lead?" + params.toString());
  }

  getProject(quoteId): Observable<QuoteSummary> { 
    return this.httpGet<QuoteSummary>("/quote/" + quoteId);
  }

  getCustomers(): Observable<Customer[]> {
    return this.httpGet<Customer[]>("/customer");
  }

  getOffices(): Observable<Office[]> {
    return this.httpGet<Office[]>("/office");
  }

  getSalespersons(): Observable<User[]> {
    return this.httpGet<User[]>("/user/salesperson");
  }

  getJobTypes(): Observable<JobType[]> {
    return this.httpGet<JobType[]>("/quote/jobType");
  }

  getStates(): Observable<string[]> {
    return this.httpGet<string[]>("/admin/states");
  }

  getLeadSourceTypes(): Observable<LeadSourceType[]> {
    return this.httpGet<LeadSourceType[]>("/customer/leadSource");
  }

  saveLead(lead: Quote): Observable<number> {
    if (lead.quote_ID == null)
      return this.httpPut<number>("/lead/", lead);
    else
      return this.httpPost<number>("/lead/" + lead.quote_ID, lead);
  }

  createQuote(quoteId: number): Observable<number> {
    return this.httpPost<number>("/lead/convert/" + quoteId, {});
  }

  expireLead(quoteId: number): Observable<number> {
    return this.httpPost<number>("/lead/expire/" + quoteId, {});
  }

  deleteLead(quoteId: number): Observable<number> {
    return this.httpDelete<number>("/lead/" + quoteId);
  }
}
